import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import Analytics from 'analytics';
import { EVENTS } from 'analytics/events';
import './team-building.css';

const PAGE = 'team_building';
const LOGO = `${process.env.PUBLIC_URL}/g2-logo.png`;

// ─────────────────────────────────────────────────────────────────
// Demo board data — workplace-flavored
// ─────────────────────────────────────────────────────────────────
const CATEGORIES = [
  'ONBOARDING LORE',
  'WORLD CAPITALS',
  'POP CULTURE 2020s',
  'OUR TEAM',
  'SCIENCE-ISH',
  'TINY HISTORY',
];

const VALUES = [200, 400, 600, 800, 1000];

const SAMPLE_CLUES = [
  { cat: 'OUR TEAM', value: 600, text: 'THIS DEPARTMENT HAS WON "MOST GIFS POSTED IN SLACK" THREE QUARTERS RUNNING.' },
  { cat: 'ONBOARDING LORE', value: 400, text: 'THE OFFICIAL NAME FOR YOUR FIRST-WEEK MEETING WHERE SOMEONE EXPLAINS THE EXPENSE TOOL FOR 45 MINUTES.' },
  { cat: 'WORLD CAPITALS', value: 800, text: 'THIS NORDIC CAPITAL SITS ON A SERIES OF ISLANDS AND IS OFTEN CALLED THE "VENICE OF THE NORTH".' },
  { cat: 'POP CULTURE 2020s', value: 200, text: 'THIS STREAMING DANCE-TREND APP TURNED A 15-SECOND CLIP INTO A FULL-BLOWN CAREER PATH.' },
];

// ─────────────────────────────────────────────────────────────────
// Hero — animated mock game board + phone overlays
// ─────────────────────────────────────────────────────────────────
function HeroBoard() {
  const [gone, setGone] = useState(new Set());
  const [flash, setFlash] = useState(null);
  const [overlay, setOverlay] = useState(null);
  const [clueIdx, setClueIdx] = useState(0);
  const intervalRef = useRef(null);

  useEffect(() => {
    let step = 0;
    const tick = () => {
      const clue = SAMPLE_CLUES[step % SAMPLE_CLUES.length];
      const key = `${clue.cat}|${clue.value}`;
      setFlash(key);
      setTimeout(() => {
        setOverlay(clue);
        setFlash(null);
      }, 350);
      setTimeout(() => {
        setOverlay(null);
        setGone(prev => new Set(prev).add(key));
      }, 3600);
      step++;
      setClueIdx(step);
    };
    tick();
    intervalRef.current = setInterval(tick, 4400);
    return () => clearInterval(intervalRef.current);
  }, []);

  const handleTileClick = (cat, val) => {
    const key = `${cat}|${val}`;
    if (gone.has(key) || overlay) return;
    setFlash(key);
    const clue = SAMPLE_CLUES.find(c => c.cat === cat && c.value === val) || SAMPLE_CLUES[0];
    setTimeout(() => {
      setOverlay({ ...clue, cat, value: val });
      setFlash(null);
    }, 350);
    setTimeout(() => {
      setOverlay(null);
      setGone(prev => new Set(prev).add(key));
    }, 4000);
  };

  return (
    <div className="tb-hero-stage">
      <div className="tb-tv-frame">
        <div className="tb-tv-bezel-light" />
        <div className="tb-board">
          {CATEGORIES.map(c => (
            <div className="tb-cat" key={c}>{c}</div>
          ))}
          {VALUES.map(v =>
            CATEGORIES.map(c => {
              const key = `${c}|${v}`;
              const isGone = gone.has(key);
              const isFlash = flash === key;
              return (
                <div
                  key={key}
                  className={`tb-tile${isGone ? ' tb-gone' : ''}${isFlash ? ' tb-flash' : ''}`}
                  onClick={() => handleTileClick(c, v)}
                >
                  {!isGone && `$${v}`}
                </div>
              );
            })
          )}
          {overlay && (
            <div className="tb-clue-overlay" key={clueIdx}>
              <div className="tb-clue-cat">{overlay.cat} · ${overlay.value}</div>
              <div className="tb-clue-text">{overlay.text}</div>
            </div>
          )}
        </div>
      </div>

      {/* Phone 1 — remote teammate answered */}
      <div className="tb-phone tb-phone-1" aria-hidden="true">
        <div className="tb-phone-screen">
          <div className="tb-phone-name">Priya · Remote</div>
          <div className="tb-phone-score">$1,800</div>
          <div className="tb-phone-input tb-typed">stockholm</div>
          <div className="tb-phone-correct">✓ CORRECT</div>
        </div>
      </div>

      {/* Phone 2 — host narration */}
      <div className="tb-phone tb-phone-2" aria-hidden="true">
        <div className="tb-phone-screen">
          <div className="tb-phone-name">Host</div>
          <div className="tb-phone-host">
            <div className="tb-h-label">🎙 Now reading</div>
            "Priya nails the Venice of the North — Stockholm. Marketing pulls ahead…"
          </div>
          <div className="tb-phone-buzz">BUZZ IN</div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// FAQ accordion — native <details> for SEO friendliness
// ─────────────────────────────────────────────────────────────────
const FAQS = [
  {
    q: 'What is virtual team building trivia?',
    plain: 'Virtual team building trivia is a trivia game a distributed team plays together over a video call to connect, break the ice, and have fun without a meeting agenda. With Groupardy, the board appears on a shared screen-share while each person answers from their own phone or laptop. An AI host reads the clues, judges answers, and keeps score, so the organizer plays along instead of running the show.',
    a: <>Virtual team building trivia is a trivia game a distributed team plays together over a video call — a low-stakes way to connect, break the ice, and have fun without a meeting agenda. With Groupardy, the board shows on your screen-share while everyone answers from their own phone or laptop. An <strong>AI host</strong> reads the clues, judges answers, and keeps score, so the organizer gets to play along instead of running the show.</>,
  },
  {
    q: 'How long does a game take?',
    plain: 'A standard Groupardy game runs about 20 to 30 minutes — short enough to fit inside a regular team meeting or the first half of a social call. You can make it shorter for a quick icebreaker or longer for a dedicated team event. Setup takes about two minutes.',
    a: <>A standard game runs about <strong>20 to 30 minutes</strong> — short enough to fit inside a regular team meeting or the first half of a social call. Shorten it for a quick icebreaker, or extend it for a dedicated team event. Setup is about two minutes.</>,
  },
  {
    q: 'How much prep does the organizer have to do?',
    plain: 'Almost none. You tell Groupardy a topic and audience level and the AI writes the full board for you — no writing questions, no building slides, no rehearsing as host. Most organizers set a game up in a couple of minutes the morning of.',
    a: <>Almost none. You give Groupardy a topic and an audience level, and the AI writes the full board — no writing questions, no building slides, no rehearsing as host. Most organizers set a game up in a couple of minutes the morning of.</>,
  },
  {
    q: 'Does it work for hybrid teams?',
    plain: 'Yes. Because everyone answers on their own device, in-office and remote teammates compete on equal footing — no one is stuck shouting over a conference-room mic. Share the board on the meeting screen-share and people in the room and at home play the same way.',
    a: <>Yes — this is where it shines. Because everyone answers on their own device, in-office and remote teammates compete on <strong>equal footing</strong>, instead of remote folks shouting over a conference-room mic. Share the board on the meeting screen and people in the room and at home play the exact same way.</>,
  },
  {
    q: 'How many people can play at once?',
    plain: 'Anywhere from 2 to 100 players in a single game. It works for a small standing team, a whole department, or a company-wide all-hands. For large groups you can score by team so breakout groups compete against each other.',
    a: <>Anywhere from <strong>2 to 100 players</strong> in a single game. It works for a small standing team, a full department, or a company-wide all-hands. For large groups, score by team so breakout groups compete against each other.</>,
  },
  {
    q: 'Can I make the trivia about our company?',
    plain: 'Yes. Give Groupardy a topic like company onboarding, your product, team inside jokes, or a new-hire\'s first week, and it builds categories around it. Company-specific boards are popular for onboarding cohorts and milestone celebrations. You can also fully customize every category and question to your exact needs.',
    a: <>Yes. Give it a topic like "company onboarding", your product, team inside jokes, or a new hire's first week, and it builds categories around it. Company-specific boards are a favorite for <strong>onboarding cohorts</strong> and milestone celebrations. You can also fully customize every category and question to your exact needs.</>,
  },
  {
    q: 'Is the content safe for work?',
    plain: 'Yes. Describe the event to the AI game builder when you create the game, and the clue topics, host tone, and humor all stay appropriate for a professional setting. Set it once; it applies to the whole game.',
    a: <>Yes. Describe the event to the AI game builder when you create the game, and the clue topics, host tone, and humor all stay appropriate for a professional setting. Set it once; it applies to the whole game.</>,
  },
  {
    q: 'Do people need to download an app or make an account?',
    plain: 'No. Players join by scanning a QR code or entering a short room code in their browser — no app, no install, no account. That keeps participation high and avoids IT friction.',
    a: <>No. Players join by scanning a QR code with their phones — no app, no install, no account. That keeps participation high and avoids IT friction.</>,
  },
  {
    q: 'Is it accessible for everyone on the team?',
    plain: 'Closed captions are on by default, the board is high-contrast and keyboard-playable, and the scoreboard is screen-reader friendly. Because answers are typed on personal devices, quieter teammates participate as fully as the loud ones.',
    a: <>Closed captions are on by default, the board is high-contrast and keyboard-playable, and the scoreboard is screen-reader friendly. And because answers are typed privately on personal devices, <strong>quieter teammates participate as fully as the loud ones</strong>.</>,
  },
  {
    q: 'Is Groupardy free for teams?',
    plain: 'You can create and play a full game for free. A paid tier (coming soon) adds custom-topic generation, larger games, saved boards, and recap analytics for teams that run trivia regularly.',
    a: <>You can create and play a full game for free. A paid tier (coming soon) adds custom-topic generation, larger games, saved boards, and recap analytics for teams that run trivia regularly.</>,
  },
];

function FAQ() {
  return (
    <div className="tb-faq-list">
      {FAQS.map((f, i) => (
        <details className="tb-faq-item" key={i} open={i === 0}>
          <summary>
            <span>{f.q}</span>
            <span className="tb-faq-toggle">+</span>
          </summary>
          <div className="tb-faq-body">{f.a}</div>
        </details>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Alternatives comparison matrix
// ─────────────────────────────────────────────────────────────────
const ALT_COLS = ['Groupardy', 'Hire a live virtual host', 'Generic quiz app', 'DIY slides + volunteer host'];
const ALT_ROWS = [
  { label: 'Cost per session', cells: ['Free', '$15–$30 / head', 'Free–$ per seat', 'Free, but staff time'] },
  { label: 'Organizer prep', cells: ['~2 minutes', 'Booking + brief', 'Hours building the quiz', 'An afternoon'] },
  { label: 'Needs a human host', cells: ['No — AI host', 'Yes (the vendor)', 'Someone reads aloud', 'A volunteer hosts the game'] },
  { label: 'Custom / company topics', cells: ['Yes, instantly', 'Sometimes, extra', 'Manual entry', 'If you write them'] },
  { label: 'Fits a 30-min meeting', cells: ['Yes', 'Often 60–90 min', 'Yes', 'Yes'] },
  { label: 'Remote Friendly (own device)', cells: ['Yes', 'Varies', 'Sometimes', 'Rarely'] },
  { label: 'Repeatable without staleness', cells: ['Fresh AI boards', 'Re-book + re-pay', 'You rebuild it', 'You rebuild it'] },
];

function AltMatrix() {
  return (
    <div className="tb-alt-wrap">
      <table className="tb-alt-table">
        <thead>
          <tr>
            <th className="tb-alt-rowhead" scope="col"><span className="tb-alt-corner">How it compares</span></th>
            {ALT_COLS.map((c, i) => (
              <th key={c} scope="col" className={i === 0 ? 'tb-alt-us' : ''}>
                {i === 0 && <span className="tb-alt-badge">Best for teams</span>}
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {ALT_ROWS.map(row => (
            <tr key={row.label}>
              <th scope="row" className="tb-alt-rowhead">{row.label}</th>
              {row.cells.map((cell, i) => (
                <td key={i} className={i === 0 ? 'tb-alt-us' : ''}>
                  {i === 0 && <span className="tb-alt-check">✓</span>}
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// JSON-LD structured data
// ─────────────────────────────────────────────────────────────────
const LD_WEBPAGE = JSON.stringify({
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Virtual Team Building Trivia for Remote Teams',
  url: 'https://groupardy.com/virtual-team-building-trivia',
  description: 'A practical guide to running virtual team building trivia for remote and hybrid teams, plus how to set up a game in under two minutes with Groupardy.',
  about: { '@type': 'Thing', name: 'Virtual team building trivia' },
  isPartOf: { '@type': 'WebSite', name: 'Groupardy', url: 'https://groupardy.com/' },
  primaryImageOfPage: 'https://groupardy.com/og-image.jpg',
});

const LD_BREADCRUMB = JSON.stringify({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Groupardy', item: 'https://groupardy.com/' },
    { '@type': 'ListItem', position: 2, name: 'Virtual Team Building Trivia', item: 'https://groupardy.com/virtual-team-building-trivia' },
  ],
});

const LD_WEBAPP = JSON.stringify({
  '@context': 'https://schema.org',
  '@type': 'WebApplication',
  name: 'Groupardy',
  url: 'https://groupardy.com/',
  applicationCategory: 'BusinessApplication',
  applicationSubCategory: 'Team Building Trivia',
  operatingSystem: 'Any (web browser)',
  description: 'Groupardy is an AI-powered virtual team building trivia game for remote and hybrid teams of 2 to 100 players. The board runs on a screen-share while players join from their phones or laptops. An AI host reads clues, judges answers, and runs the whole game — no live host to hire, no prep for the organizer.',
  offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD', availability: 'https://schema.org/InStock' },
  featureList: [
    'AI-generated trivia boards tailored to your team and topic',
    'Real-time multiplayer for 2–100 remote players on any device',
    'AI host that reads clues, judges answers, and runs the game',
    'Workplace content level with safe topics and tone',
    'Team-based scoring for departments and breakout groups',
    'Shareable recap cards and superlatives',
    'Works over Zoom, Google Meet, Microsoft Teams, or in person',
  ],
  aggregateRating: { '@type': 'AggregateRating', ratingValue: '4.9', ratingCount: '1280' },
});

const LD_FAQ = JSON.stringify({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQS.map(f => ({
    '@type': 'Question',
    name: f.q,
    acceptedAnswer: { '@type': 'Answer', text: f.plain },
  })),
});

const LD_HOWTO = JSON.stringify({
  '@context': 'https://schema.org',
  '@type': 'HowTo',
  name: 'How to run virtual team building trivia',
  description: 'Set up a virtual team building trivia game for your remote or hybrid team in under two minutes.',
  totalTime: 'PT2M',
  step: [
    { '@type': 'HowToStep', position: 1, name: 'Pick a topic and audience', text: 'Open Groupardy, tell it your team and a topic, and set the content level to Workplace. The AI writes a full board of categories and clues tuned to your group.' },
    { '@type': 'HowToStep', position: 2, name: 'Share your screen and invite the team', text: 'Share the board on your video call. Teammates scan a QR code or enter a room code in their browser to join from any device — no download.' },
    { '@type': 'HowToStep', position: 3, name: 'Let the AI host run the game', text: 'The AI host reads each clue, judges typed answers, keeps score, and crowns the winner with a shareable recap. You play along instead of hosting.' },
  ],
});

// ─────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────
const TeamBuilding = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    const params = new URLSearchParams(window.location.search);
    const referrerDomain = document.referrer
      ? (() => { try { return new URL(document.referrer).hostname; } catch { return undefined; } })()
      : undefined;
    Analytics.track(EVENTS.HOMEPAGE_VIEWED, {
      page: PAGE,
      utm_source: params.get('utm_source') || undefined,
      utm_medium: params.get('utm_medium') || undefined,
      utm_campaign: params.get('utm_campaign') || undefined,
      utm_content: params.get('utm_content') || undefined,
      referrer_domain: referrerDomain,
    });
  }, []);

  const trackCta = (location, destination) => {
    Analytics.track(EVENTS.HOMEPAGE_CTA_CLICKED, { page: PAGE, cta_location: location, destination });
  };

  return (
    <div className="teambuilding">
      <Helmet>
        <title>Virtual Team Building Trivia for Remote Teams | Groupardy</title>
        <meta name="description" content="Run virtual team building trivia your remote and hybrid team will actually look forward to. AI-built boards, a host that runs the whole game, up to 100 players on their phones. No download, two-minute setup, free to start." />
        <meta name="keywords" content="virtual team building trivia, remote team building, online trivia for work, virtual team building activities, team building games for remote teams, virtual icebreakers, hybrid team building, online team trivia, work trivia game" />
        <meta name="author" content="Groupardy" />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <link rel="canonical" href="https://groupardy.com/virtual-team-building-trivia" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://groupardy.com/virtual-team-building-trivia" />
        <meta property="og:title" content="Virtual Team Building Trivia for Remote Teams | Groupardy" />
        <meta property="og:description" content="The virtual team building trivia game that runs itself. AI-built boards, an AI host, up to 100 players on their phones. Two-minute setup, free to start." />
        <meta property="og:site_name" content="Groupardy" />
        <meta property="og:image" content="https://groupardy.com/og-image.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Virtual Team Building Trivia for Remote Teams | Groupardy" />
        <meta name="twitter:description" content="A virtual team building trivia game your team will actually want on the calendar. No download, free to start, up to 100 players." />
        <meta name="twitter:image" content="https://groupardy.com/og-image.jpg" />
        <meta name="twitter:site" content="@groupardy" />
        <meta name="theme-color" content="#2ec4b6" />
        <script type="application/ld+json">{LD_WEBPAGE}</script>
        <script type="application/ld+json">{LD_BREADCRUMB}</script>
        <script type="application/ld+json">{LD_WEBAPP}</script>
        <script type="application/ld+json">{LD_FAQ}</script>
        <script type="application/ld+json">{LD_HOWTO}</script>
      </Helmet>

      {/* ─── NAV ─── */}
      <header className="tb-nav">
        <div className="tb-wrap tb-nav-inner">
          <a className="tb-nav-brand" href="/" aria-label="Groupardy home">
            <img src={LOGO} alt="Groupardy logo" className="tb-nav-logo" />
            <span className="tb-nav-wordmark">Groupardy</span>
          </a>
          <nav className="tb-nav-links" aria-label="Primary">
            <a className="tb-nav-link" href="#benefits">Benefits</a>
            <a className="tb-nav-link" href="#how">How it works</a>
            <a className="tb-nav-link" href="#alternatives">Alternatives</a>
            <a className="tb-nav-link" href="#faq">FAQ</a>
          </nav>
          <div className="tb-nav-actions">
            <a className="tb-nav-signin" href="/login">Sign in</a>
            <a
              className="tb-btn tb-btn-primary tb-btn-sm"
              href="/create"
              onClick={() => trackCta('nav', '/create')}
            >
              Start a game
            </a>
          </div>
        </div>
      </header>

      <main>
        {/* ─── HERO ─── */}
        <section className="tb-hero" aria-labelledby="tb-hero-title">
          <div className="tb-wrap tb-hero-grid">
            <div>
              <div className="tb-hero-eyebrow">
                <span className="tb-pulse-dot" /> For remote &amp; hybrid teams
              </div>
              <h1 id="tb-hero-title">
                Virtual team building trivia<br />
                your team will <span className="tb-underlined">actually want</span> on the calendar.
              </h1>
              <p className="tb-lead">
                Groupardy is an <strong>AI-powered trivia game show</strong> built for distributed teams.
                The board runs on your screen-share, everyone plays from their own phone, and an AI host
                runs the whole thing &mdash; so the organizer plays along instead of reading questions off a slide.
              </p>
              <div className="tb-hero-cta-row">
                <a
                  className="tb-btn tb-btn-primary tb-btn-lg"
                  href="/create"
                  onClick={() => trackCta('hero_primary', '/create')}
                >
                  Start a game · Free →
                </a>
                <a className="tb-btn tb-btn-ghost tb-btn-lg" href="#how">See how it works <span aria-hidden="true">↓</span></a>
              </div>
              <div className="tb-hero-trust">
                <span><span className="tb-check">✓</span> No download for players</span>
                <span><span className="tb-check">✓</span> ~2-minute setup</span>
                <span><span className="tb-check">✓</span> Workplace-safe content</span>
                <span><span className="tb-check">✓</span> 2–100 players</span>
              </div>
            </div>
            <HeroBoard />
          </div>
        </section>

        {/* ─── STATS ─── */}
        <section className="tb-stats" aria-label="At a glance">
          <div className="tb-wrap">
            <div className="tb-stats-grid">
              <div className="tb-stat">
                <div className="tb-stat-num">~2 <span className="tb-accent2">min</span></div>
                <div className="tb-stat-label">Organizer setup</div>
              </div>
              <div className="tb-stat">
                <div className="tb-stat-num">20–30<span className="tb-accent2">m</span></div>
                <div className="tb-stat-label">Fits inside a meeting</div>
              </div>
              <div className="tb-stat">
                <div className="tb-stat-num">2 <span className="tb-accent2">→</span> 100</div>
                <div className="tb-stat-label">Players per game</div>
              </div>
              <div className="tb-stat">
                <div className="tb-stat-num">$0</div>
                <div className="tb-stat-label">To run your first game</div>
              </div>
            </div>
          </div>
        </section>

        {/* ─── BENEFITS ─── */}
        <section className="tb-section" id="benefits">
          <div className="tb-wrap">
            <div className="tb-section-head">
              <div className="tb-section-eyebrow">Why it works</div>
              <h2>Connection that doesn't feel like a mandatory meeting.</h2>
              <p className="tb-sub">
                Most remote socials die because one person has to host, prep, and somehow keep ten muted squares engaged.
                Virtual team building trivia fixes the structure: everyone has a device, everyone has a reason to pay attention,
                and nobody has to be the entertainment.
              </p>
            </div>
            <div className="tb-features">
              <div className="tb-feature">
                <div className="tb-feature-icon">🕒</div>
                <h4>Zero prep for the organizer</h4>
                <p>Type a topic, share some details about your team and interest, and the AI builds the whole trivia board. No question-writing, no slide deck, no being the host who can't also play.</p>
              </div>
              <div className="tb-feature">
                <div className="tb-feature-icon">⚖️</div>
                <h4>REMOTE FRIENDLY BY DESIGN</h4>
                <p>Everyone answers on their own phone, so the person dialing in from home competes on the same footing as the five people in the conference room.</p>
              </div>
              <div className="tb-feature">
                <div className="tb-feature-icon">🙋</div>
                <h4>Quiet people play too</h4>
                <p>Answers are typed privately, not shouted over each other. Introverts and new hires participate fully without having to grab the mic.</p>
              </div>
              <div className="tb-feature">
                <div className="tb-feature-icon">🔁</div>
                <h4>Doesn't get stale</h4>
                <p>Fresh AI-built boards every time means a recurring trivia slot stays fun in week 12, not just week one. No re-buying, no rebuilding.</p>
              </div>
              <div className="tb-feature">
                <div className="tb-feature-icon">🏷️</div>
                <h4>Make it about your team</h4>
                <p>Build categories from onboarding facts, your product, or team inside jokes. Perfect for welcoming a new-hire cohort or marking a milestone.</p>
              </div>
              <div className="tb-feature">
                <div className="tb-feature-icon">🎯</div>
                <h4>Time-boxed, not open-ended</h4>
                <p>A game lands in 20–30 minutes, so it slots into an existing standup, all-hands, or social call without blowing up anyone's calendar.</p>
              </div>
            </div>
          </div>
        </section>

        {/* ─── HOW IT WORKS ─── */}
        <section className="tb-section" id="how" style={{ background: 'var(--cream-soft)' }}>
          <div className="tb-wrap">
            <div className="tb-section-head">
              <div className="tb-section-eyebrow">How it works</div>
              <h2>Three steps, about two minutes, no rehearsal.</h2>
              <p className="tb-sub">If you can share your screen on a video call, you can run this.</p>
            </div>
            <div className="tb-steps">
              <div className="tb-step">
                <div className="tb-step-illus" aria-hidden="true">
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, fontFamily: 'var(--font-brand)', fontSize: '0.7rem', letterSpacing: '0.16em', color: 'var(--teal-deep)' }}>
                    <span style={{ padding: '4px 10px', background: '#fff', border: '1.5px solid var(--teal)', borderRadius: 999 }}>"COMPANY ONBOARDING"</span>
                    <span style={{ padding: '4px 10px', background: '#fff', border: '1.5px dashed var(--line-strong)', borderRadius: 999, color: 'var(--ink-soft)' }}>"WORLD CAPITALS"</span>
                    <span style={{ padding: '4px 10px', background: 'var(--gold)', border: '1.5px solid var(--gold)', borderRadius: 999, color: 'var(--ink)' }}>WORKPLACE · MEDIUM</span>
                  </div>
                </div>
                <div className="tb-step-num">1</div>
                <h3>Pick a topic</h3>
                <p>Tell Groupardy your team and interests. The AI writes a full board of categories and clues in under five minutes.</p>
              </div>

              <div className="tb-step">
                <div className="tb-step-illus" aria-hidden="true">
                  <div className="tb-mini-tv">
                    {Array.from({ length: 25 }).map((_, i) => <div className="tb-t" key={i} />)}
                  </div>
                  <div style={{ position: 'absolute', bottom: 10, display: 'flex', gap: 6 }}>
                    <div className="tb-mini-phone"><div className="tb-ps" /></div>
                    <div className="tb-mini-phone tb-b"><div className="tb-ps" /></div>
                    <div className="tb-mini-phone tb-c"><div className="tb-ps" /></div>
                    <div className="tb-mini-phone"><div className="tb-ps" /></div>
                  </div>
                </div>
                <div className="tb-step-num">2</div>
                <h3>Share &amp; invite</h3>
                <p>Share the board on your Zoom, Meet, or Teams call. The team scans a QR code to join from any device. No downloads or logins needed.</p>
              </div>

              <div className="tb-step">
                <div className="tb-step-illus" aria-hidden="true">
                  <div className="tb-mini-host">
                    <div className="tb-mh-bar" />
                    <div className="tb-mh-bar tb-s" />
                    <div className="tb-mh-bar tb-t" />
                    <div className="tb-mh-bar tb-s" />
                    <div className="tb-mh-bar tb-t" />
                  </div>
                </div>
                <div className="tb-step-num">3</div>
                <h3>Let the host run it</h3>
                <p>The AI host reads each clue, judges typed answers (close enough counts), keeps score, and ends with a shareable recap. You play along.</p>
              </div>
            </div>
          </div>
        </section>

        {/* ─── WHEN TO RUN IT ─── */}
        <section className="tb-section" id="use-cases">
          <div className="tb-wrap">
            <div className="tb-section-head">
              <div className="tb-section-eyebrow">When to run it</div>
              <h2>Useful more often than you'd think.</h2>
              <p className="tb-sub">Same game, a few different jobs — each one tuned to the moment.</p>
            </div>
            <div className="tb-cases">
              <div className="tb-case tb-case-1">
                <div className="tb-case-icon">👋</div>
                <h4>New-hire onboarding</h4>
                <p>An "Our Company" board helps a cohort learn names, values, and where the good docs live — without a slideshow.</p>
              </div>
              <div className="tb-case tb-case-2">
                <div className="tb-case-icon">📅</div>
                <h4>Recurring team socials</h4>
                <p>A standing 20-minute slot that stays fresh because the board is new every time. Set it and forget the prep.</p>
              </div>
              <div className="tb-case tb-case-3">
                <div className="tb-case-icon">🧊</div>
                <h4>Meeting icebreakers</h4>
                <p>Open an all-hands or a kickoff with five minutes of trivia to wake the room up before the agenda.</p>
              </div>
              <div className="tb-case tb-case-4">
                <div className="tb-case-icon">🌍</div>
                <h4>Distributed offsites</h4>
                <p>Team-based scoring lets breakout groups across time zones compete head-to-head in one game.</p>
              </div>
              <div className="tb-case tb-case-5">
                <div className="tb-case-icon">🎉</div>
                <h4>Milestones &amp; wins</h4>
                <p>Celebrate a launch or work anniversary with a custom board about the project or the person.</p>
              </div>
              <div className="tb-case tb-case-6">
                <div className="tb-case-icon">🤝</div>
                <h4>Cross-team mixers</h4>
                <p>Mix departments into teams so people who never share a meeting finally talk to each other.</p>
              </div>
            </div>
          </div>
        </section>

        {/* ─── ALTERNATIVES ─── */}
        <section className="tb-section tb-compare-band" id="alternatives">
          <div className="tb-wrap">
            <div className="tb-section-head tb-center">
              <div className="tb-section-eyebrow" style={{ justifyContent: 'center', display: 'inline-flex' }}>Alternatives</div>
              <h2>How it stacks up against the other options.</h2>
              <p className="tb-sub" style={{ margin: '0 auto' }}>
                There are a few ways to run virtual team building trivia. Here's an honest look at the trade-offs,
                so you can pick what fits your team and budget.
              </p>
            </div>
            <AltMatrix />
            <div className="tb-alt-notes">
              <div className="tb-alt-note">
                <h4>Hire a live virtual host</h4>
                <p>Great production value and a real person MC-ing, but it's the priciest option, needs booking lead time, and usually runs 60–90 minutes — more event than meeting.</p>
              </div>
              <div className="tb-alt-note">
                <h4>Generic quiz apps</h4>
                <p>Fine for a quick poll, but someone still reads questions aloud, content is multiple-choice and canned, and you build the quiz yourself each time.</p>
              </div>
              <div className="tb-alt-note">
                <h4>DIY slides + a volunteer host</h4>
                <p>Free in dollars, expensive in time — one teammate spends an afternoon building it and then can't actually play because they're hosting.</p>
              </div>
            </div>
          </div>
        </section>

        {/* ─── FAQ ─── */}
        <section className="tb-section tb-faq-band" id="faq">
          <div className="tb-wrap">
            <div className="tb-section-head tb-center">
              <div className="tb-section-eyebrow" style={{ justifyContent: 'center', display: 'inline-flex' }}>FAQ</div>
              <h2>Questions HR and team leads actually ask.</h2>
            </div>
            <FAQ />
          </div>
        </section>

        {/* ─── FINAL CTA ─── */}
        <section className="tb-final-cta" id="cta">
          <div className="tb-wrap tb-final-cta-inner">
            <div className="tb-section-eyebrow">Get started</div>
            <h2>Run trivia your team looks forward to.</h2>
            <p className="tb-sub">
              Build a workplace-safe board, share the link with the team, and let the AI host take it from there.
              Free to start, no card, about two minutes to your first clue.
            </p>
            <div className="tb-final-cta-row">
              <a
                className="tb-btn tb-btn-gold tb-btn-lg"
                href="/create"
                onClick={() => trackCta('final_cta', '/create')}
              >
                Start a game · Free →
              </a>
              <a className="tb-btn tb-btn-ghost tb-btn-lg" href="#how" style={{ color: '#fff', borderColor: 'rgba(255,255,255,0.3)' }}>See how it works</a>
            </div>
            <div className="tb-final-trust">
              <span>No credit card</span>
              <span>·</span>
              <span>No download for players</span>
              <span>·</span>
              <span>Workplace-safe</span>
              <span>·</span>
              <span>Up to 100 players</span>
            </div>
          </div>
        </section>
      </main>

      {/* ─── FOOTER ─── */}
      <footer className="tb-footer">
        <div className="tb-wrap">
          <div className="tb-foot-grid">
            <div className="tb-foot-brand">
              <div className="tb-wm">
                <img src={LOGO} alt="" style={{ width: 28, height: 28 }} />
                Groupardy
              </div>
              <p>The AI-powered trivia game show for groups and teams. Virtual team building, minus the awkward.</p>
              <div className="tb-foot-socials">
                <a href="https://www.instagram.com/groupardy/" target="_blank" rel="noopener noreferrer" aria-label="Instagram">📷</a>
                <a href="https://x.com/groupardy" target="_blank" rel="noopener noreferrer" aria-label="X (Twitter)">𝕏</a>
              </div>
            </div>
            <div className="tb-foot-col">
              <h5>Play</h5>
              <ul>
                <li><a href="/create" onClick={() => trackCta('footer', '/create')}>Start a game</a></li>
                <li><a href="/">Home</a></li>
              </ul>
            </div>
            <div className="tb-foot-col">
              <h5>For teams</h5>
              <ul>
                <li><a href="#benefits">Why it works</a></li>
                <li><a href="#how">How it works</a></li>
                <li><a href="#alternatives">Alternatives</a></li>
                <li><a href="#faq">FAQ</a></li>
              </ul>
            </div>
            <div className="tb-foot-col">
              <h5>Company</h5>
              <ul>
                <li><a href="/about">About</a></li>
                <li><a href="/blog">Blog</a></li>
              </ul>
            </div>
            <div className="tb-foot-col">
              <h5>Support</h5>
              <ul>
                <li><a href="#faq">FAQ</a></li>
                <li><a href="/terms">Terms</a></li>
                <li><a href="/privacy">Privacy</a></li>
              </ul>
            </div>
          </div>
          <div className="tb-foot-bar">
            <div>© 2026 Groupardy. All rights reserved.</div>
            <div>Made for the team chat that keeps saying "we should do something fun".</div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TeamBuilding;
